import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ArchiveBoxIcon, ArchiveBoxUpIcon } from '@rsos/assets/icons';
import {
  setResourcePanelView,
  useGetTransportsQuery,
  useSelectedRequestId,
  useSetTransportQueueCategoryMutation,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  formatTransportStatuses,
  getResourceRequestQueueCategory,
} from '../../../../AirAmbulanceForm/etl/etlStatusUtils';
import { useResourceRequestClock } from '../useResourceRequestClock';
import styles from './QueueCategoryToggleButton.module.css';

/**
 * Header button that moves the open transport to the Closed queue section, or
 * back to Active when it is already closed. Account-wide via the queue-category
 * override; renders nothing until the open transport is in the cache.
 */
export function QueueCategoryToggleButton() {
  const dispatch = useDispatch();
  const selectedRequestId = useSelectedRequestId();
  const { data } = useGetTransportsQuery();
  const nowMs = useResourceRequestClock();
  const [setQueueCategory] = useSetTransportQueueCategoryMutation();

  const transport = data?.transports.find(
    t => t.gmr_transport_id === selectedRequestId,
  );

  const isClosed = transport
    ? getResourceRequestQueueCategory(
        transport,
        formatTransportStatuses(transport.statuses),
        nowMs,
      ) === 'closed'
    : false;

  const transportId = transport?.id;
  const handleClick = useCallback(() => {
    if (transportId === undefined) {
      return;
    }
    setQueueCategory({
      transportId,
      category: isClosed ? 'active' : 'closed',
    });
    dispatch(setResourcePanelView('list_requests'));
  }, [transportId, isClosed, setQueueCategory, dispatch]);

  if (!transport) {
    return null;
  }

  return (
    <button
      type="button"
      className={styles.toggleButton}
      onClick={handleClick}
      title={isClosed ? 'Move to Active queue' : 'Move to Closed queue'}
      aria-label={isClosed ? 'Move to Active queue' : 'Move to Closed queue'}
      data-name="resource-panel-queue-category-toggle"
    >
      {isClosed ? (
        <ArchiveBoxUpIcon width={12} height={12} />
      ) : (
        <ArchiveBoxIcon width={12} height={12} />
      )}
    </button>
  );
}
